/* eslint-disable max-lines-per-function */
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@e2n/ui';
import { useProfile } from '@/hooks';
import { cn } from '@/lib/utils';
import { faImage, faNewspaper } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Fragment } from 'react/jsx-runtime';
import { useUpdateNewsStatus } from '@/domain/navigation/features/navigation/hooks/useUpdateNewsStatus/useUpdateNewsStatus';
import { useNews } from '@/domain/navigation/features/navigation/hooks/useNews/useNews';

export function NewsFeedSubMenu() {
  const { news } = useNews();
  const { t } = useTranslation();
  const location = useLocation();
  const [updateNewsStatus] = useUpdateNewsStatus();
  const profile = useProfile();
  const navigate = useNavigate();
  const unreadItemsCount = news.filter((item) => !item.istGelesen).length;

  function markNewsItemAsReadById(id: string) {
    updateNewsStatus({ newsItemId: id, istGelesen: true })
      .catch((err) => {
        Sentry.captureException(`Could not update news status: ${err}`, {
          tags: { email: profile.manager?.email },
        });
      })
      .finally(() => {
        navigate(`/news/${id}`, { state: { background: location } });
      });
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="flex items-center justify-between">
        <div className="w-full flex items-center gap-2">
          <FontAwesomeIcon icon={faNewspaper} />
          {t('general.navigation.profileDropdown.news')}
        </div>
        {unreadItemsCount > 0 && (
          <div className="rounded-full w-5 h-4 bg-primary-blue-600 text-white text-[10px] flex items-center justify-center">
            {unreadItemsCount}
          </div>
        )}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="px-2 py-3 max-w-66 max-h-75 overflow-auto">
          {news.map((item) => (
            <Fragment key={item.id}>
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  markNewsItemAsReadById(item.id);
                }}
                to={{
                  pathname: `/news/${item.id}`,
                }}>
                <DropdownMenuItem
                  key={item.id}
                  className="relative flex items-center gap-3">
                  <Avatar className="w-8 h-8">
                    <AvatarImage
                      className="object-cover object-left"
                      src={item.bildUrl}
                      alt={item.titel}
                    />
                    <AvatarFallback>
                      <FontAwesomeIcon
                        className="text-neutrals-800"
                        icon={faImage}
                        size="lg"
                      />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-0.5 items-start text-sm flex-1 min-w-0 mr-4">
                    <div
                      className={cn(
                        'truncate w-full',
                        item.istGelesen ? 'font-normal' : 'font-semibold',
                      )}>
                      {item.titel}
                    </div>
                    <div className="text-neutrals-700">
                      {item.veroeffentlichtAmRelative()}
                    </div>
                  </div>

                  {!item.istGelesen && (
                    <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary-blue-600" />
                  )}
                </DropdownMenuItem>
              </Link>
              <DropdownMenuSeparator className="last:hidden m-0" />
            </Fragment>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
